import React from "react";

import {
  ArrowRight as ArrowRightIcon,
  Mail as MailIcon,
  Unlock as UnlockIcon,
  Upload,
  UserCheck as UserCheckIcon,
} from "react-feather";
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import pluralize from "pluralize";

import TodaysThingsToDoSkeleton from "./components/Skeleton";

import { ThingsToDoProps } from "./types";

import { useTealium } from "../../../context/TealiumProvider/TealiumProvider";

const useStyles = makeStyles(theme => ({
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  noItems: {
    flexGrow: 1,
    margin: theme.spacing(1.5, 0),
    textAlign: "center",
    color: theme.palette.grey[500],
  },
  rightIcon: {
    minWidth: "unset",
    display: "inline-flex",
    flexShrink: 0,
  },
  itemLink: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    color: "inherit",
    "&:hover": {
      textDecoration: "none",
      color: "inherit",
    },
  },
}));

export const NOTIFICATION_TYPES = {
  APPROVE_LEARNER: {
    icon: <UserCheckIcon />,
    type: "learner",
    message: "new learners",
    url: "/learners",
  },
  SERVICE_MESSAGE: {
    icon: <MailIcon />,
    type: "message",
    message: "from service",
    url: "/messages",
  },
  OPEN_CAP: {
    icon: <UnlockIcon />,
    type: "Open your cap",
    message: "to get more learners",
    url: "/settings/preferences",
  },
};

const TodaysThingsToDo: React.FC<ThingsToDoProps> = ({
  loading = false,
  notifications = [],
  outstandingDocuments = [],
}) => {
  const classes = useStyles();
  const { trackEvent } = useTealium();
  const history = useHistory();

  if (loading) {
    return <TodaysThingsToDoSkeleton />;
  }

  return (
    <Card>
      <List>
        {outstandingDocuments.map((document, i) => (
          <ListItem
            data-test="FADocument-item"
            key={document.FADocumentKey}
            divider={i !== outstandingDocuments.length - 1}
          >
            <Link
              className={classes.itemLink}
              to={{
                pathname: "/settings/my-details",
                hash: "documents",
              }}
            >
              <ListItemIcon>
                <Upload />
              </ListItemIcon>
              <ListItemText>
                <b>Upload your</b> {document.documentType}
              </ListItemText>
              <div className={classes.rightIcon}>
                <ArrowRightIcon />
              </div>
            </Link>
          </ListItem>
        ))}
        {notifications.map(notification => (
          <ListItem data-test="notification-item" key={notification.id}>
            <ListItemIcon>
              {NOTIFICATION_TYPES[notification.type].icon}
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">
                <span className={classes.fontWeightMedium}>
                  {notification.count}
                </span>{" "}
                <span className={classes.fontWeightMedium}>
                  {pluralize(
                    NOTIFICATION_TYPES[notification.type].type,
                    notification.count || 1,
                  )}
                </span>{" "}
                {NOTIFICATION_TYPES[notification.type].message}
              </Typography>
            </ListItemText>
            <ListItemSecondaryAction>
              <Tooltip title="View">
                <IconButton
                  onClick={() => {
                    trackEvent({
                      eventCategory: "Homepage",
                      eventAction: "Interaction",
                      eventLabel: "View Today's Things To Do",
                    });
                    history.push(NOTIFICATION_TYPES[notification.type].url);
                  }}
                >
                  <ArrowRightIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {notifications.length ? (
          <ListItem className={classes.noItems}>
            <Typography
              data-test="no-notifications-text"
              className={classes.noItems}
            >
              You don’t have any reminders today.
            </Typography>
          </ListItem>
        ) : null}
      </List>
    </Card>
  );
};

export default TodaysThingsToDo;
